import React from "react";
import ContactUs from "./ContactUs";
import EnquiryForm from "./forms/EnquiryForm";
import "../styles/contact-us-section.css";

/**
 * ContactUsSection
 * ---------------------------------------------------
 * Hero banner + green info band (cu-band), then a 2-col
 * lower section (cu-lower) with:
 *  - left: floating-ish form card (cu-card)
 *  - right: Google Maps embed (cu-map)
 *
 * Props:
 * - heroImage: string (public path or URL)
 * - address, email, phone: strings
 * - title, subtitle: strings for form card header
 * - formVariant: "contact" | "enrolment"
 * - enquiryEndpoint: optional POST endpoint override for EnquiryForm
 * - showMap: boolean (default true)
 * - mapQuery: string used in Google Maps embed (defaults to address)
 * - mapTitle: iframe title (a11y)
 */
export default function ContactUsSection({
  heroImage = "",
  address = "13–15 The Avenue\nFerntree Gully VIC 3156",
  email = "office@theavenueschool.vic.edu.au",
  phone = "(03) 9758 7859",
  title = "Contact us",
  subtitle = "Have a question? Get in touch:",
  formVariant = "contact", // "contact" | "enrolment"
  enquiryEndpoint,
  showMap = true,
  mapQuery, // if omitted, we fallback to address
  mapTitle = "The Avenue School on Google Maps",
}) {
  const mapsQuery = (mapQuery || address || "").replace(/\n/g, " ");
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(
    mapsQuery
  )}&output=embed`;

  return (
    <section className="cu-wrap">
      {/* Banner: grey slab + hero image */}
      <div className="cu-banner">
        <div className="cu-bg" />
        <img className="cu-hero" src={heroImage} alt="" aria-hidden="true" />

        {/* Green information band */}
        <div className="cu-band">
          <div className="cu-band__group">
            <p className="cu-band__eyebrow">Address</p>
            <p className="cu-band__text">{address}</p>
          </div>

          <div className="cu-band__group">
            <p className="cu-band__eyebrow">Contact Us</p>
            <p className="cu-band__text">{email}</p>
            <p className="cu-band__text cu-band__text--tight">{phone}</p>
          </div>
        </div>
      </div>

      {/* Lower area: form card + map */}
      <div className="cu-lower">
        <div className="cu-card" aria-labelledby="cu-title">
          <div className="cu-card__header">
            <h3 id="cu-title" className="cu-card__title">
              {title}
            </h3>
            <p className="cu-card__subtitle">{subtitle}</p>
          </div>

          {/* Switch between the original Contact form and repurposed Enrolments form */}
          {formVariant === "enrolment" ? (
            <EnquiryForm
              endpoint={enquiryEndpoint}
              buttonLabel="Send enquiry"
              compact
            />
          ) : (
            <ContactUs embedded />
          )}
        </div>

        {showMap && (
          <div className="cu-map">
            <iframe
              title={mapTitle}
              src={mapSrc}
              loading="lazy"
              width= "100%"
              height="100%"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        )}
      </div>
    </section>
  );
}

